import { Box, Button, Tooltip, Typography } from "@outschool/backpack";
import {
  GetLearnerModeTransferTokenMutation,
  GetLearnerModeTransferTokenMutationVariables,
  PickLearnerQuery
} from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { enrolledPath } from "@outschool/routes";
import { useMutation } from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import { Modal } from "@outschool/ui-legacy-component-library";
import _ from "lodash";
import React from "react";
import { useHistory } from "react-router-dom";

import * as Routes from "../../../shared/Routes";
import ActionType from "../../actions/ActionType";
import { usePickLearner } from "../../hooks/JoinMeeting";
import { getLearnerModeTransferToken } from "../../queries/AuthenticationQueries";
import { useAppState } from "../../stores/AppStateProvider";
import Link from "../Link";

export default function PickLearner({
  sectionUid,
  onClose,
  isClub = false,
  hideForAdmin = false
}: {
  sectionUid: string;
  onClose?: () => void;
  isClub?: boolean;
  hideForAdmin?: boolean;
}): React.ReactElement {
  const history = useHistory();
  const { data, loading, error } = usePickLearner(sectionUid);
  const { t } = useTranslation("client\\components\\enrolled\\PickLearner");
  if (loading) {
    // @ts-ignore TS(2322): Type 'null' is not assignable to type 'ReactElemen... Remove this comment to see the full error message
    return null;
  }

  if (!loading && error) {
    // TODO: Show the error message in modal
    // OsPlatform.captureError(error);
    // @ts-ignore TS(2322): Type 'null' is not assignable to type 'ReactElemen... Remove this comment to see the full error message
    return null;
  }

  const canManage = data?.classroom?.section.currentUserCanManage;
  const learnersToPick = data?.learnersToPick;

  // In certain views, when a learnerId has not been identified, the picker auto-opens.
  // This behavior should not happen for section admins.
  // Ideally, we would check canManage and render conditionally from the parent component,
  // but one of the parents is OutageJoinLiveMeetingButton, so instead we check here.
  if (hideForAdmin && canManage) {
    // @ts-ignore TS(2322): Type 'null' is not assignable to type 'ReactElemen... Remove this comment to see the full error message
    return null;
  }

  const formatPartialEnrollment = (learner: $TSFixMe) => {
    return {
      uid: learner.enrollmentUid,
      learner_uid: learner.uid
    };
  };
  const returnPath = enrolledPath(sectionUid);

  const redirectPath = (learner: $TSFixMe) => {
    if (isClub) {
      return Routes.learnerAppClubUrl(sectionUid);
    }

    return Routes.learnerAppClassroomUrl(
      sectionUid,
      formatPartialEnrollment(learner)
    );
  };

  return (
    <Modal open onClose={onClose ? onClose : () => history.push(returnPath)}>
      <Box
        flex
        sx={{
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between"
        }}
      >
        <Typography
          variant="h2"
          sx={{
            marginBottom: "0.5em"
          }}
        >
          {isClub ? t("Enter Club") : t("Enter Classroom")}
        </Typography>
      </Box>
      <Box
        sx={{
          textAlign: "center"
        }}
      >
        {t("Select which learner is joining.")}
      </Box>
      <Box
        sx={{
          paddingY: "2em"
        }}
      >
        {_.orderBy(
          _.uniqBy(learnersToPick, l => l.uid),
          l => l.age,
          // eslint-disable-next-line i18next/no-literal-string
          "desc"
        ).map(l => (
          <Box
            key={l.enrollmentUid}
            sx={{
              marginBottom: "1em"
            }}
          >
            <ClassroomButton classroomPath={redirectPath(l)} learner={l} />
          </Box>
        ))}
        {!onClose && <Link to={returnPath}>{t("Cancel")}</Link>}
      </Box>
    </Modal>
  );
}

interface ClassroomButtonProps {
  classroomPath: string;
  learner: PickLearnerQuery["learnersToPick"][number];
}

const ClassroomButton = ({ classroomPath, learner }: ClassroomButtonProps) => {
  const { currentUser } = useSession();
  const appState = useAppState();
  const { t } = useTranslation("client\\components\\enrolled\\PickLearner");

  const [getTransferToken] = useMutation<
    GetLearnerModeTransferTokenMutation,
    GetLearnerModeTransferTokenMutationVariables
  >(getLearnerModeTransferToken);

  const handleEnterClassroomClick = async (
    learnerUid: string,
    classroomPath: $TSFixMe
  ) => {
    const { data } = await getTransferToken({ variables: { learnerUid } });

    appState.appDispatcher.dispatch(ActionType.User.REDIRECT_TO_LEARNER_APP, {
      transferToken: data?.learnerModeTransferToken?.transferToken,
      authRequirement: currentUser?.learnerModeSwitchAuthRequirement,
      learnerAppUrl: classroomPath
    });
  };

  return (
    <Box>
      <Tooltip
        title={
          learner.isEnrollmentPaused
            ? t("{{learnerName}}’s enrollment is paused", {
                learnerName: learner.name
              })
            : null
        }
      >
        <span>
          <Button
            variant="contained"
            fullWidth
            disabled={learner.isEnrollmentPaused}
            onClick={() =>
              handleEnterClassroomClick(learner.uid, classroomPath)
            }
          >
            {learner.name}
          </Button>
        </span>
      </Tooltip>
    </Box>
  );
};
